// ── Shared profile menu (migrated from PhoenixDesign src/profile-menu.jsx) ──
// Presentational, but app-wired: takes the real `user` ({name,email,role}),
// links to the REAL app page routes, gates the Admin group + Candle Browser by
// role, and signs out via POST /api/auth/logout. Other pages reuse this as-is.
import React, { useState } from 'react';

// ── theme (light/dark) — persisted to localStorage, applied to <html data-theme>.
// Each page's shell sets data-theme early (inline <head> script) so there's no flash;
// this toggle flips it live + persists. Key matches that shell script: dt_theme.
function getTheme() {
  try { return localStorage.getItem('dt_theme') === 'light' ? 'light' : 'dark'; } catch (_) { return 'dark'; }
}
function applyTheme(mode) {
  document.documentElement.setAttribute('data-theme', mode);
  try { localStorage.setItem('dt_theme', mode); } catch (_) {}
}

// Design label → real app route. Items without href are still vanilla pages not
// yet migrated; the href is the existing page so the menu works during migration.
const MENU = [
  { group: 'Trading', items: [
    { icon: '◍', label: 'Trading Dashboard', href: '/trading-dashboard' },
    { icon: '◔', label: 'Daily P&L Dashboard', href: '/pnl-dashboard' },
    { icon: '◫', label: 'P&L Reports', href: '/reports/pnl' },
  ] },
  { group: 'Tools', items: [
    { icon: '⌗', label: 'Zen Terminal', href: '/chart-viewer' },
    { icon: '▦', label: 'Layouts Generator', href: '/layouts-generator' },
    { icon: '☰', label: 'Daily Analysis', href: '/daily-analysis' },
    { icon: '⌂', label: 'Brokers', href: '/brokers' },
    { icon: '⚙', label: 'Settings', href: '/settings' },
    // ONE entry, not two. /membership is for somebody who has a plan and /billing is for
    // somebody choosing one; the membership page links onward to the plans when there is
    // nothing yet, so this single door works in both states.
    { icon: '₹', label: 'Membership', href: '/membership' },
    { icon: '✦', label: 'Refer and earn', href: '/membership#refer' },
    { icon: '❖', label: 'Partner programme', href: '/partners' },
  ] },
  { group: 'Admin', admin: true, items: [
    { icon: '⚇', label: 'User Management', href: '/admin', admin: true },
    { icon: '◎', label: 'Leads', href: '/admin/leads', admin: true },
    { icon: '☰', label: 'Surveys', href: '/admin/surveys', admin: true },
    { icon: '▶', label: 'Recordings', href: '/admin/recordings', admin: true },
    { icon: '⚒', label: 'Admin Settings', href: '/admin-settings', admin: true },
    { icon: '▦', label: 'Plans & Features', href: '/admin-plans', admin: true },
    { icon: '₹', label: 'Payments', href: '/admin-payments', admin: true },
    { icon: '↗', label: 'Business', href: '/admin-business', admin: true },
    { icon: '▥', label: 'Candle Browser', href: '/candle-browser', admin: true },
  ] },
  { group: 'Account', items: [
    { icon: '◉', label: 'My Profile', href: '/my-profile' },
    { icon: '✆', label: 'Talk to us', action: 'talk' },
    { icon: '⏻', label: 'Sign out', danger: true, action: 'logout' },
  ] },
];

function initialsOf(name, email) {
  const src = (name || '').trim() || (email || '').trim();
  if (!src) return '··';
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

async function logout() {
  // Forget the "Talk to us" chat too, so the next person on this browser never sees this conversation.
  try { if (window.$chatwoot) window.$chatwoot.reset(); } catch (_) { /* chat not loaded */ }
  document.cookie.split(';').map((c) => c.trim().split('=')[0]).filter((n) => n.startsWith('cw_'))
    .forEach((n) => { document.cookie = n + '=; Max-Age=0; path=/'; });
  try { await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }); }
  catch (_) { /* sign out regardless */ }
  window.location.replace('/login');
}

// "Talk to us" (chat / WhatsApp / contact form) — the same panel as the website's floating button,
// from public/js/talk-to-us.js, loaded the first time somebody asks for it.
function talkToUs() {
  if (window.zensiblyTalk) { window.zensiblyTalk.open(); return; }
  const s = document.createElement('script');
  s.src = '/js/talk-to-us.js';
  s.onload = () => window.zensiblyTalk && window.zensiblyTalk.open();
  s.onerror = () => { window.location.href = '/contact'; };
  document.head.appendChild(s);
}

export default function ProfileMenu({ active = 'Trading Dashboard', user }) {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(getTheme);
  const setMode = (m) => { setTheme(m); applyTheme(m); };
  const isAdmin = user && (user.role === 'admin' || user.role === 'super_admin');
  const name = (user && user.name) || (user && user.email) || 'Account';
  const email = (user && user.email) || '';
  const role = (user && user.role) || '';

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen((o) => !o)} aria-label="Profile menu" style={{
        width: 36, height: 36, borderRadius: 99, flex: 'none', cursor: 'pointer', display: 'grid', placeItems: 'center',
        border: '1px solid ' + (open ? 'color-mix(in oklab, var(--accent) 50%, var(--border))' : 'var(--border)'),
        background: 'color-mix(in oklab, var(--accent) 20%, var(--surface2))', color: 'var(--text)', fontWeight: 700, fontSize: 13, fontFamily: 'inherit',
      }}>{initialsOf(user && user.name, email)}</button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 160 }} />
          <div style={{
            position: 'absolute', right: 0, top: 'calc(100% + 8px)', zIndex: 170, width: 270, maxHeight: '78vh', overflow: 'auto',
            background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 6,
            boxShadow: '0 26px 60px -18px rgba(0,0,0,0.6)',
          }}>
            <div style={{ padding: '12px 12px 14px', borderBottom: '1px solid var(--border)', marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 15, fontWeight: 750, letterSpacing: '-0.01em' }}>{name}</span>
                {role && role !== 'user' && <span className="mono" style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)' }}>({role})</span>}
              </div>
              {email && <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 2 }}>{email}</div>}
            </div>
            {/* theme toggle */}
            <div style={{ padding: '2px 6px 6px' }}>
              <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.09em', color: 'var(--muted)', fontWeight: 700, padding: '2px 4px 5px' }}>Appearance</div>
              <div style={{ display: 'flex', gap: 3, background: 'var(--inset)', border: '1px solid var(--border)', borderRadius: 8, padding: 3 }}>
                {[{ k: 'light', icon: '☀', label: 'Light' }, { k: 'dark', icon: '☾', label: 'Dark' }].map((o) => {
                  const on = theme === o.k;
                  return (
                    <button key={o.k} onClick={() => setMode(o.k)} style={{
                      flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      padding: '5px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                      fontSize: 12, fontWeight: 600, background: on ? 'var(--accent)' : 'transparent',
                      color: on ? 'var(--accent-ink)' : 'var(--muted)',
                    }}><span style={{ fontSize: 12.5, lineHeight: 1 }}>{o.icon}</span>{o.label}</button>
                  );
                })}
              </div>
            </div>
            {MENU.filter((sec) => !sec.admin || isAdmin).map((sec) => (
              <div key={sec.group} style={{ marginBottom: 2 }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.09em', color: 'var(--muted)', fontWeight: 700, padding: '8px 10px 5px' }}>{sec.group}</div>
                {sec.items.filter((it) => !it.admin || isAdmin).map((it) => {
                  const isActive = it.label === active;
                  const itemStyle = {
                    display: 'flex', alignItems: 'center', gap: 11, width: '100%', padding: '9px 10px', borderRadius: 9,
                    border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: isActive ? 700 : 550, textAlign: 'left', textDecoration: 'none', boxSizing: 'border-box',
                    background: isActive ? 'color-mix(in oklab, var(--accent) 14%, transparent)' : 'transparent',
                    color: it.danger ? 'var(--loss)' : isActive ? 'var(--accent)' : 'var(--text)',
                    boxShadow: isActive ? 'inset 2px 0 0 var(--accent)' : 'none',
                  };
                  const inner = (
                    <>
                      <span style={{ width: 20, textAlign: 'center', fontSize: 14, color: it.danger ? 'var(--loss)' : isActive ? 'var(--accent)' : 'var(--muted)' }}>{it.icon}</span>
                      {it.label}
                    </>
                  );
                  if (it.action === 'talk') {
                    return <button key={it.label} onClick={() => { setOpen(false); talkToUs(); }} style={itemStyle}>{inner}</button>;
                  }
                  if (it.action === 'logout') {
                    return <button key={it.label} onClick={logout} style={itemStyle}>{inner}</button>;
                  }
                  return <a key={it.label} href={it.href} style={itemStyle}>{inner}</a>;
                })}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
